import { useState, useEffect, useRef, useCallback } from 'react';
import { ref, onValue, set } from 'firebase/database';
import { db } from '../config/firebase';
import { SLIDER_INTERVAL, SCROLL_THRESHOLD } from '../config/constants';

/* ── Firebase: galeria ─────────────────────────────── */
export function useGaleria() {
  const [galeria, setGaleria] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onValue(
      ref(db, 'galeria'),
      s => { setGaleria(s.val() || {}); setLoading(false); },
      e => { console.error(e); setLoading(false); }
    );
    return () => unsub();
  }, []);

  const saveGaleria = useCallback(async d => {
    try { await set(ref(db, 'galeria'), d); }
    catch (e) { console.error(e); }
  }, []);

  return { galeria, setGaleria, saveGaleria, loading };
}

/* ── Firebase: contingut editable ──────────────────── */
const DEFAULT_CONTINGUT = {
  intro: {
    fotos: ['foto1.png','foto2.png','foto3.png','foto4.png','foto5.png','foto6.png'],
  },
  quiSom: {
    titol: 'Qui som?',
    text: "El Casalot és un esplai laic i inclusiu d'educació en el lleure on infants, monitores i monitors comparteixen una educació en valors, crítica i lliure. Un lloc on els infants poden desconnectar de la rutina i gaudir d'un temps de trobada enriquidor.",
    pills: ['Laic', 'Inclusiu', 'Participatiu', 'Divertit'],
  },
  queFem: {
    titol: 'Què fem?',
    text: 'Organitzem activitats cada dissabte a la tarda, colònies, campaments i projectes per fomentar el creixement personal i col·lectiu dels infants i joves.',
    activitats: [
      { icon: '🎭', titol: 'Activitats setmanals', desc: 'Cada dissabte a la tarda, dinàmiques, jocs i tallers' },
      { icon: '🏕️', titol: 'Colònies i campaments', desc: 'Sortides i estades per descobrir el món junts' },
      { icon: '🌍', titol: 'Projectes de valors', desc: 'Reflexió, compromís social i creixement personal' },
    ],
    fotos: ['foto1.png','foto2.png','foto3.png','foto4.png','foto5.png','foto6.png'],
  },
  onSom: {
    titol: 'On som?',
    mapaUrl: 'assets/mapa.png',
    mapsLink: 'https://www.google.es/maps/place/Esplai+El+Casalot/@41.9802206,2.3085711,236m/data=!3m2!1e3!5s0x12a52f53c39d1ca7:0xdc584534014b1da!4m6!3m5!1s0x12a52f0e4f4496eb:0xc93ccc031614850e!8m2!3d41.9799582!4d2.3088258!16s%2Fg%2F11h9ktwd0y?hl=ca&entry=ttu',
  },
  esplac: {
    titol: 'Esplac',
    intro: "Som un esplai associat a Esplais Catalans (Esplac), una entitat laica, progressista i transformadora que agrupa esplais d'arreu del territori català.",
    valorsIntro: 'Compartim uns valors comuns:',
    valors: [
      "L'educació en el lleure com a espai educatiu lliure i crític.",
      "El feminisme, l'ecologisme i l'antiracisme com a eixos transversals.",
      "L'autogestió i l'assemblearisme, donant veu a infants, joves i monitores.",
      "El compromís social, participant activament en la comunitat.",
    ],
    outro: "A través d'Esplac, ens formem, compartim experiències i participem en trobades que enriqueixen el nostre projecte educatiu.",
  },
  contacte: {
    titol: 'Vine amb nosaltres',
    subtitol: "T'esperem! Contacta'ns per qualsevol dubte o per apuntar el teu fill o filla.",
    email: 'esplaielcasalot@gmail.com',
    instagram: 'esplaielcasalot',
  },
  footer: { text: '2025 Esplai El Casalot · Tots els drets reservats' },
};

export function useContingut() {
  const [contingut, setContingut] = useState(null);

  useEffect(() => {
    const unsub = onValue(
      ref(db, 'contingut'),
      snap => {
        const d = snap.val();
        if (d) {
          setContingut({
            ...DEFAULT_CONTINGUT, ...d,
            intro:   { ...DEFAULT_CONTINGUT.intro,   ...d.intro,
              fotos: d.intro?.fotos || DEFAULT_CONTINGUT.intro.fotos },
            quiSom:  { ...DEFAULT_CONTINGUT.quiSom,  ...d.quiSom  },
            queFem:  { ...DEFAULT_CONTINGUT.queFem,  ...d.queFem,
              activitats: d.queFem?.activitats || DEFAULT_CONTINGUT.queFem.activitats },
            esplac:  { ...DEFAULT_CONTINGUT.esplac,  ...d.esplac,
              valors: d.esplac?.valors || DEFAULT_CONTINGUT.esplac.valors },
            contacte:{ ...DEFAULT_CONTINGUT.contacte,...d.contacte },
          });
        } else {
          setContingut(DEFAULT_CONTINGUT);
          set(ref(db, 'contingut'), DEFAULT_CONTINGUT);
        }
      },
      e => { console.error(e); setContingut(DEFAULT_CONTINGUT); }
    );
    return () => unsub();
  }, []);

  const saveContingut = useCallback(async d => {
    try { await set(ref(db, 'contingut'), d); }
    catch (e) { console.error(e); }
  }, []);

  return { contingut, setContingut, saveContingut };
}

/* ── Scroll reveal ─────────────────────────────────── */
export function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('visible'); obs.unobserve(e.target); }
      }),
      { threshold: 0.1 }
    );
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  });
}

/* ── Header scroll ─────────────────────────────────── */
export function useScrolled() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);
  return scrolled;
}

/* ── Slider ────────────────────────────────────────── */
export function useSlider(count) {
  const [current, setCurrent] = useState(0);
  const timer = useRef(null);

  const next = useCallback(() => setCurrent(c => (c + 1) % count), [count]);
  const prev = useCallback(() => setCurrent(c => (c - 1 + count) % count), [count]);
  const restart = useCallback(() => {
    clearInterval(timer.current);
    timer.current = setInterval(next, SLIDER_INTERVAL);
  }, [next]);

  useEffect(() => { restart(); return () => clearInterval(timer.current); }, [restart]);

  return {
    current, setCurrent,
    goNext: () => { next(); restart(); },
    goPrev: () => { prev(); restart(); },
  };
}

/* ── Parallax relatiu a l'element (correcte a qualsevol punt de la pàgina) ──
   Només calcula mentre l'element és visible (IntersectionObserver + rAF loop),
   això estalvia feina de CPU quan l'usuari és lluny de la secció.
   Retorna [ref, offsetPx] — cal enganxar `ref` a l'element pare. ── */
export function useElementParallax(factor = 0.12) {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = ref.current;
    if (!el) return;

    let raf = null;
    let active = false;

    const tick = () => {
      if (!active) return;
      const rect = el.getBoundingClientRect();
      const center = rect.top + rect.height / 2 - window.innerHeight / 2;
      setOffset(-center * factor);
      raf = requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(([entry]) => {
      active = entry.isIntersecting;
      if (active && !raf) raf = requestAnimationFrame(tick);
      if (!active && raf) { cancelAnimationFrame(raf); raf = null; }
    }, { threshold: 0 });

    io.observe(el);
    return () => { io.disconnect(); if (raf) cancelAnimationFrame(raf); };
  }, [factor]);

  return [ref, offset];
}

/* ── Barra de progrés de scroll ── */
export function useScrollProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const h = document.documentElement.scrollHeight - window.innerHeight;
        setProgress(h > 0 ? Math.min(100, (window.scrollY / h) * 100) : 0);
        raf = null;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return progress;
}